import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { SkeletonModule } from "primeng/skeleton";
import { CardModule } from "primeng/card";

/**
 * Skeleton loader for Customer Edit page
 * Matches the layout of customer-edit.component.html
 */
@Component({
  selector: "app-customer-edit-skeleton",
  standalone: true,
  imports: [CommonModule, SkeletonModule, CardModule],
  template: `
    <div class="customer-edit-skeleton">
      <!-- Page Header Skeleton -->
      <div class="page-header-skeleton">
        <div class="header-left-skeleton">
          <p-skeleton width="2rem" height="2rem" borderRadius="50%" />
          <div class="title-section-skeleton">
            <p-skeleton width="10rem" height="1.125rem" />
            <p-skeleton width="5rem" height="0.75rem" />
          </div>
        </div>
        <div class="header-right-skeleton">
          <p-skeleton width="4.5rem" height="2rem" borderRadius="4px" />
        </div>
      </div>

      <!-- Accordion Content Skeleton -->
      <div class="accordion-skeleton">
        <!-- Basic Info Accordion -->
        <div class="accordion-panel-skeleton">
          <div class="accordion-header-skeleton">
            <p-skeleton width="1rem" height="1rem" />
            <p-skeleton width="5rem" height="0.875rem" />
          </div>
          <div class="accordion-content-skeleton">
            <div class="form-grid-skeleton">
              <div class="field-skeleton">
                <p-skeleton width="5rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.25rem" />
              </div>
              <div class="field-skeleton">
                <p-skeleton width="5rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.25rem" />
              </div>
              <div class="field-skeleton">
                <p-skeleton width="4rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.25rem" />
              </div>
              <div class="field-skeleton">
                <p-skeleton width="4rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.25rem" />
              </div>
              <div class="field-skeleton">
                <p-skeleton width="5rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.25rem" />
              </div>
              <div class="field-skeleton">
                <p-skeleton width="5rem" height="0.625rem" />
                <p-skeleton width="100%" height="2.25rem" />
              </div>
            </div>
          </div>
        </div>

        <!-- Addresses Accordion -->
        <div class="accordion-panel-skeleton">
          <div class="accordion-header-skeleton">
            <p-skeleton width="1rem" height="1rem" />
            <p-skeleton width="5.5rem" height="0.875rem" />
          </div>
          <div class="accordion-content-skeleton">
            <div class="addresses-skeleton">
              <!-- Shipping Side -->
              <div class="address-side-skeleton">
                <div class="address-title-skeleton">
                  <p-skeleton width="1rem" height="1rem" />
                  <p-skeleton width="7rem" height="0.875rem" />
                </div>
                <p-skeleton
                  width="100%"
                  height="1.75rem"
                  styleClass="checkbox-skeleton"
                />
                <div class="address-grid-skeleton">
                  <div class="field-skeleton">
                    <p-skeleton width="5rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton">
                    <p-skeleton width="5rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton full-width">
                    <p-skeleton width="6rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton">
                    <p-skeleton width="4rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton">
                    <p-skeleton width="4rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                </div>
              </div>
              <!-- Billing Side -->
              <div class="address-side-skeleton billing-side">
                <div class="address-title-skeleton">
                  <p-skeleton width="1rem" height="1rem" />
                  <p-skeleton width="6rem" height="0.875rem" />
                </div>
                <p-skeleton
                  width="100%"
                  height="1.75rem"
                  styleClass="checkbox-skeleton"
                />
                <div class="address-grid-skeleton">
                  <div class="field-skeleton">
                    <p-skeleton width="5rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton">
                    <p-skeleton width="5rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton full-width">
                    <p-skeleton width="6rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton">
                    <p-skeleton width="4rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                  <div class="field-skeleton">
                    <p-skeleton width="4rem" height="0.625rem" />
                    <p-skeleton width="100%" height="2.25rem" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Methods Accordion -->
        <div class="accordion-panel-skeleton">
          <div class="accordion-header-skeleton">
            <p-skeleton width="1rem" height="1rem" />
            <p-skeleton width="7.5rem" height="0.875rem" />
            <p-skeleton
              width="1.25rem"
              height="0.875rem"
              styleClass="ml-auto"
              borderRadius="9999px"
            />
          </div>
          <div class="accordion-content-skeleton payment-content">
            <div class="payment-grid-skeleton">
              <div class="payment-item-skeleton">
                <p-skeleton width="2rem" height="2rem" borderRadius="0.5rem" />
                <div class="payment-details-skeleton">
                  <p-skeleton width="8rem" height="0.875rem" />
                  <p-skeleton width="5rem" height="0.625rem" />
                </div>
              </div>
              <div class="payment-item-skeleton">
                <p-skeleton width="2rem" height="2rem" borderRadius="0.5rem" />
                <div class="payment-details-skeleton">
                  <p-skeleton width="7rem" height="0.875rem" />
                  <p-skeleton width="4rem" height="0.625rem" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        overflow: hidden;
        min-height: 0;
      }

      .customer-edit-skeleton {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        background-color: white;
        overflow: hidden;
      }

      .page-header-skeleton {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.5rem 0.75rem;
        background: white;
        border-bottom: 1px solid #e5e7eb;
        flex-shrink: 0;
      }

      .header-left-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .title-section-skeleton {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .header-right-skeleton {
        display: flex;
        align-items: center;
      }

      .accordion-skeleton {
        flex: 1;
        overflow-y: auto;
        padding: 0.75rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .accordion-panel-skeleton {
        background: white;
        border-radius: 0.375rem;
        border: 1px solid #e5e7eb;
        overflow: hidden;
      }

      .accordion-header-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.625rem 0.75rem;
        border-bottom: 1px solid #e5e7eb;
      }

      .accordion-content-skeleton {
        padding: 0.75rem;
      }

      .form-grid-skeleton {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
        max-width: 800px;
      }

      .field-skeleton {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
      }

      .field-skeleton.full-width {
        grid-column: 1 / -1;
      }

      .addresses-skeleton {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
      }

      .address-side-skeleton {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .address-side-skeleton.billing-side {
        border-left: 1px solid #e5e7eb;
        padding-left: 1.5rem;
      }

      .address-title-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid #f3f4f6;
      }

      ::ng-deep .checkbox-skeleton {
        border-radius: 0.25rem;
      }

      .address-grid-skeleton {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.625rem;
      }

      .payment-content {
        padding: 0.5rem;
      }

      .payment-grid-skeleton {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.5rem;
      }

      .payment-item-skeleton {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
      }

      .payment-details-skeleton {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      ::ng-deep .ml-auto {
        margin-left: auto;
      }

      /* Responsive - Tablet */
      @media (max-width: 768px) {
        .page-header-skeleton {
          padding: 0.5rem;
        }

        .accordion-skeleton {
          padding: 0.5rem;
        }

        .accordion-header-skeleton {
          padding: 0.5rem 0.625rem;
        }

        .accordion-content-skeleton {
          padding: 0.5rem;
        }

        .form-grid-skeleton {
          grid-template-columns: 1fr;
          gap: 0.625rem;
        }

        .addresses-skeleton {
          grid-template-columns: 1fr;
          gap: 1rem;
        }

        .address-side-skeleton.billing-side {
          border-left: none;
          padding-left: 0;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .address-grid-skeleton {
          grid-template-columns: 1fr;
        }

        .payment-grid-skeleton {
          grid-template-columns: 1fr;
        }
      }

      /* PrimeNG Skeleton animation override */
      ::ng-deep .p-skeleton {
        background: linear-gradient(
          90deg,
          #f0f0f0 25%,
          #e0e0e0 50%,
          #f0f0f0 75%
        );
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
      }

      @keyframes loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `,
  ],
})
export class CustomerEditSkeletonComponent {}
